import { observable } from "@legendapp/state";
import { ObservablePersistLocalStorage } from "@legendapp/state/persist-plugins/local-storage";
import { synced } from "@legendapp/state/sync";
import { toast } from "sonner";

import { authClient } from "~/libs/auth.lib";

export const auth$ = observable(
  synced({
    initial: null,
    // A transient session fetch failure must never read as "logged out". A
    // thrown `get` makes Legend treat it as a failed sync (keeps the last
    // value + retries) rather than resolving to `null` and clearing auth$.
    retry: {
      infinite: true,
      delay: 1000,
      backoff: "exponential",
      maxDelay: 30_000,
    },
    persist: {
      name: "auth",
      plugin: ObservablePersistLocalStorage,
    },
    get: async () => {
      const { data, error } = await authClient.getSession();
      // Only a clean response updates auth$: a real session, or a confirmed
      // `null` (unauthenticated). On a transient failure (500/network/deploy
      // blip) `getSession` returns `{ data: null, error }` — throw so Legend
      // retains the last-known session and retries instead of signing the
      // user out.
      if (error) {
        throw new Error(error.message ?? "Failed to fetch session");
      }
      return data;
    },
    set: async ({ value, changes }) => {
      const validChanges = changes.some((change) =>
        ["user/name", "user/image"].includes(change.pathStr),
      );
      if (!validChanges) return;

      const { error } = await authClient.updateUser({
        name: value?.user.name,
        image: value?.user.image,
      });

      if (error) {
        toast.error(error.message || "Failed to update user");
        throw new Error(error.message, { cause: error });
      }
    },
  }),
);

// Subscribe to better-auth's atom and sync to Legend State
// This automatically updates when better-auth refetches (login, logout, etc.)
authClient.$store.atoms.session.subscribe((state) => {
  if (state.data) {
    auth$.set(state.data);
  } else if (!state.isPending && (!state.error || state.error.status === 401)) {
    // Only clear on a confirmed unauthenticated signal: a definitive null
    // response (200) or an explicit 401. Better Auth's atom preserves the
    // previous `data` on transient (non-401) errors, so those keep the
    // session above and never reach here -- this guard just documents the
    // intent and stays correct if that behavior ever changes.
    auth$.set(null);
  }
});

// Eagerly fetch the canonical session from the server at module load. The
// subscription above pipes the result into `auth$`. `__root.beforeLoad`
// no longer awaits this -- it reads the localStorage-persisted `auth$`
// synchronously so the root match never enters `status: "pending"` with
// an unset `loadPromise` (the `throw undefined` race in
// `node_modules/.pnpm/@tanstack+react-router@*/Match.js` MatchInner).
// Guarded because TanStack Start loads modules server-side even with
// `ssr: false`.
if (typeof window !== "undefined") {
  authClient.getSession();
}
